/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

type ViewportLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type MeasureCallback = (
  x: number,
  y: number,
  width: number,
  height: number,
  pageX: number,
  pageY: number,
) => void;

type MeasurableNode = {
  measure?: (callback: MeasureCallback) => void;
  measureInWindow?: (
    callback: (x: number, y: number, width: number, height: number) => void,
  ) => void;
};

const getViewportScrollOffset = () => {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 };
  }

  return {
    x: window.scrollX ?? window.pageXOffset ?? 0,
    y: window.scrollY ?? window.pageYOffset ?? 0,
  };
};

// Normalizes RN measurement APIs to viewport coordinates so modal/popover
// positioning stays aligned with the trigger after the page scrolls.
export const measureInViewport = (
  node: MeasurableNode | null | undefined,
  onMeasure: (layout: ViewportLayout) => void,
) => {
  if (!node) return false;

  // `measureInWindow` already reports viewport-relative coordinates.
  if (typeof node.measureInWindow === 'function') {
    node.measureInWindow((x, y, width, height) => {
      onMeasure({ x, y, width, height });
    });
    return true;
  }

  if (typeof node.measure === 'function') {
    node.measure((_x, _y, width, height, pageX, pageY) => {
      const scrollOffset = getViewportScrollOffset();

      // `measure` returns page coordinates, so we subtract the current scroll
      // to convert them into the viewport space used by RN `Modal`.
      onMeasure({
        x: pageX - scrollOffset.x,
        y: pageY - scrollOffset.y,
        width,
        height,
      });
    });
    return true;
  }

  return false;
};

export type { ViewportLayout };
